import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote, priceNotes } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update the notes ("Extra 10% off up to $75 applied on checkout", the sale
    // cap footnote). They render OUTSIDE the price container — see the comment in
    // snippets/price.liquid — so replacing the container leaves them stale.
    const newNotes = newProductPrice.querySelector('[ref="priceNotes"]');

    if (!newNotes) {
      priceNotes?.remove();
    } else if (!priceNotes) {
      // Queried live: both `volumePricingNote` and `priceContainer` may have
      // just been swapped out above, leaving the captured refs detached.
      const anchor =
        this.querySelector('[ref="volumePricingNote"]') ?? this.querySelector('[ref="priceContainer"]');
      anchor?.insertAdjacentElement('afterend', /** @type {Element} */ (newNotes.cloneNode(true)));
    } else {
      priceNotes.replaceWith(newNotes);
    }

    const variantId = event.detail.resource?.id ?? '';

    // Update installments (SPI banner) on the price block, if enabled there
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = variantId;
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // PDP bottom payment-terms snippet: form ids `product-form-installment-pdp-*` (outside product-price)
    const sectionWrapper = /** @type {HTMLElement|null} */ (
      this.closest('[id^="shopify-section-"]')
    );
    if (sectionWrapper) {
      sectionWrapper
        .querySelectorAll(
          `form.payment-terms[id^="product-form-installment-pdp-"] input[name="id"]`
        )
        .forEach((el) => {
          if (!(el instanceof HTMLElement) || el.closest('product-price')) return;
          const node = /** @type {HTMLInputElement} */ (el);
          node.value = variantId;
          node.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
